/* eslint-disable camelcase */

import * as Sentry from '@sentry/browser';
import { html, LitElement, nothing } from 'lit';
import { fetchRequest } from 'Utils/api-fetch-request';
import { getAgo } from 'Utils/text';

import Styles from './vm-video-comments-list-item.scss';

export default class VmVideoCommentsListItem extends LitElement {
    static styles = Styles;

    static properties = {
        comment: { type: Object },
        dataAddCommentsPerm: { type: Boolean },
        dataMarkFeaturedPerm: { type: Boolean },
        videoSlug: { type: String },
        isReply: { type: Boolean },
    };

    constructor() {
        super();
        this.isReply = false;
    }

    connectedCallback() {
        super.connectedCallback();

        this._onLightboxClick = (e) => {
            const anchor = e.target.closest('.js_commentAttachmentItem a');
            if (!anchor) return;

            e.preventDefault();

            if (!window.$?.fn?.ekkoLightbox) {
                return;
            }

            window.$(anchor).ekkoLightbox();
        };

        (this.shadowRoot ?? this).addEventListener('click', this._onLightboxClick);
    }

    disconnectedCallback() {
        (this.shadowRoot ?? this).removeEventListener('click', this._onLightboxClick);
        super.disconnectedCallback();
    }

    commentPublishedDate(comment) {
        const publishedDate = new Date(comment.when);
        return getAgo(publishedDate);
    }

    handleCommentUpdate() {
        this.dispatchEvent(
            new CustomEvent('comment-updated', {
                bubbles: true,
                composed: true,
            }),
        );
    }

    handleThumbsUpClick() {
        if (!this.comment) return;

        const { id, user_has_thumbed_up, thumbs_up_count } = this.comment;
        const url = `/api/videos/${this.videoSlug}/comments/${id}/thumbup/`;
        const method = user_has_thumbed_up ? 'DELETE' : 'POST';

        // Mutate in place to avoid triggering parent re-render overwriting child state
        this.comment.user_has_thumbed_up = !user_has_thumbed_up;
        this.comment.thumbs_up_count = !user_has_thumbed_up
            ? (thumbs_up_count || 0) + 1
            : Math.max((thumbs_up_count || 0) - 1, 0);
        this.requestUpdate();

        fetchRequest(url, { method }).catch((error) => {
            Sentry.captureException(error);
            // Rollback on error
            this.comment.user_has_thumbed_up = user_has_thumbed_up;
            this.comment.thumbs_up_count = thumbs_up_count;
            this.requestUpdate();
        });
    }

    handleFeatureClick() {
        if (!this.comment) return;

        const { id, featured } = this.comment;
        const url = `/api/videos/${this.videoSlug}/comments/${id}/`;

        // Optimistic update for star icon
        this.comment.featured = !featured;
        this.requestUpdate();

        fetchRequest(url, { method: 'PATCH', body: JSON.stringify({ featured: !featured }) })
            .then(() => {
                // Still refetch to update the featured comments list
                this.handleCommentUpdate();
            })
            .catch((error) => {
                Sentry.captureException(error);
                // Rollback on error
                this.comment.featured = featured;
                this.requestUpdate();
            });
    }

    renderAttachments(attachments) {
        const attachmentItems = attachments.map((attachment) => {
            if (attachment.mimetype.includes('image')) {
                return html`
                    <div class="comment__attachment-item comment__attachment-item--image js_commentAttachmentItem">
                        <a href="${attachment.url}">
                            <img src="${attachment.url}" alt="Attachment Image" class="img-thumbnail img-polaroid" />
                        </a>
                    </div>
                `;
            } else {
                return html`
                    <div class="comment__attachment-item">
                        <iframe
                            src="https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(attachment.url)}"
                            width="300"
                            height="300"
                            seamless
                            style="border: none;"
                        ></iframe>
                    </div>
                `;
            }
        });

        return html` <div class="comment__attachments-list">${attachmentItems}</div> `;
    }

    renderReplies() {
        if (!this.comment?.children?.length) return nothing;

        return html`
            <ul class="comment__replies-list">
                ${this.comment.children.map(
                    (child) => html`
                        <vm-video-comments-list-item
                            .comment=${child}
                            .dataAddCommentsPerm=${this.dataAddCommentsPerm}
                            .dataMarkFeaturedPerm=${this.dataMarkFeaturedPerm}
                            .videoSlug=${this.videoSlug}
                            .isReply=${true}
                        ></vm-video-comments-list-item>
                    `,
                )}
            </ul>
        `;
    }

    render() {
        const comment = this.comment;
        if (!comment) return nothing;

        return html`
            <li class="comment ${this.isReply ? 'comment--reply' : ''}">
                <div class="comment__inner-wrap">
                    <div class="comment__profile-image">
                        <vm-profile-image
                            .imageSrc=${comment.author.avatar}
                            .imageAlt="${comment.author.first_name} ${comment.author.last_name}"
                            .userName=${comment.author.first_name}
                            .link=${comment.author.url}
                        ></vm-profile-image>
                    </div>
                    <div class="comment__content-wrap">
                        <div class="comment__header">
                            <a
                                href="${comment.author.url}"
                                title="${comment.author.first_name} ${comment.author.last_name}"
                            >
                                ${comment.author.first_name} ${comment.author.last_name}
                            </a>
                            <small>${this.commentPublishedDate(comment)}</small>
                            ${this.dataMarkFeaturedPerm
                                ? html`
                                      <vm-button
                                          variant="text-only"
                                          size="md"
                                          custom="feature-comment"
                                          class="js_commentFeatureButton"
                                          @click=${this.handleFeatureClick}
                                      >
                                          <span
                                              class="${comment.featured ? 'icon-star-full' : 'icon-star-empty'}"
                                          ></span>
                                      </vm-button>
                                  `
                                : nothing}
                        </div>
                        <div class="comment__main">
                            <p>${comment.comment}</p>
                            ${comment.attachments?.length ? this.renderAttachments(comment.attachments) : nothing}
                        </div>
                        <div class="comment__footer">
                            ${this.dataAddCommentsPerm
                                ? html`
                                      <vm-button-link href="${comment.reply_url}" title="Reply" custom="comment-cta">
                                          Reply
                                      </vm-button-link>
                                  `
                                : nothing}
                            <vm-button-link href="${comment.pm_url}" title="Message" custom="comment-cta">
                                Message
                            </vm-button-link>
                            <vm-button
                                custom="comment-cta thumb-up"
                                ?active=${comment.user_has_thumbed_up}
                                @click=${this.handleThumbsUpClick}
                            >
                                <i class="icon icon-thumbs-up"></i>
                                ${comment.thumbs_up_count > 0
                                    ? html` <span class="thumbup-count">${comment.thumbs_up_count}</span> `
                                    : nothing}
                                Thumbs Up
                            </vm-button>
                        </div>
                    </div>
                </div>
                ${this.renderReplies()}
            </li>
        `;
    }
}
